/**
 * useTransactions Hook
 *
 * React hook for transaction data with loading states.
 * Supports filtering, search, and pagination.
 */

import { useState, useEffect, useCallback } from 'react';
import { Transaction } from '@services/db';
import { transactionService, TransactionFilter } from '@services/transactions';
import { dataEvents } from '@services/dataEvents';

interface UseTransactionsResult {
  transactions: Transaction[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  hasMore: boolean;
  loadMore: () => Promise<void>;
}

interface UseTransactionsOptions {
  accountId?: string;
  type?: Transaction['type'];
  categoryId?: string;
  search?: string;
  startDate?: Date;
  endDate?: Date;
  amountMin?: number;
  amountMax?: number;
  limit?: number;
}

const DEFAULT_LIMIT = 50;

/**
 * Hook to fetch and manage transactions data.
 */
export function useTransactions(options: UseTransactionsOptions = {}): UseTransactionsResult {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const limit = options.limit || DEFAULT_LIMIT;

  const fetchTransactions = useCallback(async (reset = true) => {
    try {
      setLoading(true);
      setError(null);

      const filter: TransactionFilter = {
        accountId: options.accountId,
        type: options.type,
        categoryId: options.categoryId,
        search: options.search,
        startDate: options.startDate,
        endDate: options.endDate,
        amountMin: options.amountMin,
        amountMax: options.amountMax,
        limit: limit + 1, // Fetch one extra to check if there's more
        offset: reset ? 0 : offset,
      };

      const data = await transactionService.getFiltered(filter);

      // Check if there are more results
      const fetchedHasMore = data.length > limit;
      setHasMore(fetchedHasMore);

      // Only take the limit amount
      const results = fetchedHasMore ? data.slice(0, limit) : data;

      if (reset) {
        setTransactions(results);
        setOffset(results.length);
      } else {
        setTransactions((prev) => [...prev, ...results]);
        setOffset((prev) => prev + results.length);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load transactions';
      setError(message);
      console.error('[useTransactions] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [options.accountId, options.type, options.categoryId, options.search, options.startDate, options.endDate, options.amountMin, options.amountMax, limit, offset]);

  useEffect(() => {
    setOffset(0);
    fetchTransactions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.accountId, options.type, options.categoryId, options.search, options.startDate?.getTime(), options.endDate?.getTime(), options.amountMin, options.amountMax]);

  // Auto-refresh when transactions data changes (from other sources like sync or QuickAdd)
  useEffect(() => {
    const unsubscribe = dataEvents.on('transactions', () => {
      setOffset(0);
      fetchTransactions(true);
    });
    return unsubscribe;
  }, [fetchTransactions]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchTransactions(false);
  }, [fetchTransactions, hasMore, loading]);

  const refresh = useCallback(async () => {
    setOffset(0);
    await fetchTransactions(true);
  }, [fetchTransactions]);

  return {
    transactions,
    loading,
    error,
    refresh,
    hasMore,
    loadMore,
  };
}

/**
 * Hook to get recent transactions.
 */
export function useRecentTransactions(limit = 10) {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await transactionService.getRecent(limit);
      setTransactions(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load transactions';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-refresh when transactions data changes
  useEffect(() => {
    const unsubscribe = dataEvents.on('transactions', refresh);
    return unsubscribe;
  }, [refresh]);

  return { transactions, loading, error, refresh };
}
